/**
 * Orchestration: turning a phrase into a reply.
 *
 * Sits in `adapters` rather than `core` because it reaches the network. The decision it makes
 * is the app's whole personality, so it is worth stating outright:
 *
 * - **If the bundled corpus has something genuinely relevant, use that.** A proverb that
 *   answers what you actually said beats a random joke every time.
 * - **If nothing matches, go and get a fresh joke from the internet.** This is where the live
 *   APIs earn their place — not as the default, but as the thing that happens when the local
 *   corpus has nothing to say.
 * - **If that fails too, fall back to a random line from the corpus** and label it as random.
 *
 * So the network is an enhancement, never a dependency. Pull the cable and the app still
 * answers everything; it just stops surprising you.
 */

import { findMatches, MIN_SCORE, pickReply } from '../core/match.js';
import type { Lang, Reply } from '../core/types.js';
import { fetchLiveLine } from './liveJokes.js';

export interface ReplyRequest {
  input: string;
  lang: Lang;
  /** Entry ids to avoid, so the same line does not come back twice running. */
  exclude?: readonly string[];
  /** Whether the live APIs may be called. */
  useLiveSources: boolean;
  /**
   * Two numbers in [0, 1), supplied by the caller so the core stays pure and the whole flow
   * stays reproducible in a test.
   */
  randomValues: [number, number];
}

export interface ReplyResult {
  reply: Reply;
  /** How the reply was reached, for the small "why this answer" line under it. */
  route: 'matched' | 'live' | 'random';
}

export async function respond(request: ReplyRequest): Promise<ReplyResult | undefined> {
  const { input, lang, exclude = [], useLiveSources, randomValues } = request;

  const matches = findMatches(input, { lang, exclude });
  const best = matches[0];

  if (best && best.score >= MIN_SCORE) {
    const reply = pickReply(input, { lang, exclude, randomValue: randomValues[0] });
    if (reply) return { reply, route: 'matched' };
  }

  // Nothing in the corpus fits. This is exactly when a fresh line from the internet is worth
  // the wait — and the live sources are English-only, so Russian skips straight to the corpus.
  if (useLiveSources && lang === 'en') {
    const live = await fetchLiveLine(randomValues[1]);
    if (live) {
      return {
        reply: {
          text: live.text,
          lang,
          attribution: live.attribution,
          source: 'api',
          matchedTerms: [],
          random: true,
        },
        route: 'live',
      };
    }
  }

  const fallback = pickReply(input, { lang, exclude, randomValue: randomValues[0] });
  return fallback ? { reply: fallback, route: 'random' } : undefined;
}
